import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Radio, Shield, ChevronLeft, ExternalLink, RefreshCw, Lock } from "lucide-react";
import { listGroups } from "@/lib/api";

const HealthBadge = ({ score }) => {
    let cls = "text-emerald-300 bg-emerald-500/10 border-emerald-500/20";
    if (score < 90 && score >= 70) cls = "text-cyan-300 bg-cyan-500/10 border-cyan-500/20";
    else if (score < 70 && score >= 40) cls = "text-amber-300 bg-amber-500/10 border-amber-500/20";
    else if (score < 40) cls = "text-rose-300 bg-rose-500/10 border-rose-500/20";
    return (
        <Badge
            variant="outline"
            className={cls}
            data-testid="channel-health-score-badge"
        >
            <span dir="ltr" className="font-mono">{score}%</span>
        </Badge>
    );
};

export default function Channels() {
    const [channels, setChannels] = useState([]);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();

    const load = async () => {
        try {
            const data = await listGroups();
            const all = data.groups || [];
            setChannels(all.filter((g) => g.chat_type === "channel"));
        } catch (_) {
            // ignore network errors; periodic retry handles recovery
        }
        setLoading(false);
    };

    useEffect(() => {
        load();
        const i = setInterval(load, 30000);
        return () => clearInterval(i);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div className="space-y-4 md:space-y-6">
            <div className="flex flex-wrap items-end justify-between gap-3">
                <div>
                    <h1 className="text-2xl md:text-3xl font-semibold tracking-tight">
                        القنوات المحمية
                    </h1>
                    <p className="mt-1 text-sm text-muted-foreground">
                        {channels.length} قناة متصلة • مكافحة السبام، فلاتر، AI، نسخ احتياطي
                    </p>
                </div>
                <Button
                    variant="outline"
                    size="sm"
                    onClick={load}
                    className="gap-2"
                    data-testid="channels-refresh-button"
                >
                    <RefreshCw className="h-4 w-4" />
                    تحديث
                </Button>
            </div>

            {loading ? (
                <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
                    {[1, 2, 3].map((i) => (
                        <Skeleton key={i} className="h-32" />
                    ))}
                </div>
            ) : channels.length === 0 ? (
                <Card className="bg-card/40">
                    <CardContent className="p-12 text-center">
                        <div className="mx-auto inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-cyan-500/10 border border-cyan-500/20">
                            <Radio className="h-7 w-7 text-cyan-300" />
                        </div>
                        <h3 className="mt-4 text-lg font-semibold">
                            لا توجد قنوات محمية بعد
                        </h3>
                        <p className="mt-1 text-sm text-muted-foreground max-w-md mx-auto">
                            أضف Guardian Pro كأدمن في قناتك مع صلاحيات حذف الرسائل
                            ليبدأ بمكافحة السبام، فلترة الروابط، وكشف الإحتيال بالـ AI.
                        </p>
                        <div className="mt-5 flex flex-wrap justify-center gap-2">
                            <Button
                                className="gap-2"
                                onClick={() =>
                                    window.open(
                                        "https://t.me/cinjago_monitor_bot",
                                        "_blank",
                                    )
                                }
                                data-testid="channels-open-bot-button"
                            >
                                <ExternalLink className="h-4 w-4" />
                                افتح البوت
                            </Button>
                            <Button
                                variant="outline"
                                className="gap-2"
                                onClick={() =>
                                    window.open(
                                        "https://telegram.org/blog/admin-revolution",
                                        "_blank",
                                    )
                                }
                            >
                                كيف تضيف بوت لقناة؟
                            </Button>
                        </div>
                    </CardContent>
                </Card>
            ) : (
                <div
                    className="grid gap-3 md:grid-cols-2 lg:grid-cols-3"
                    data-testid="channels-grid"
                >
                    {channels.map((c) => {
                        const settings = c.settings || {};
                        const protectionActive = !!settings.protection_active;
                        const paused = !!settings.protection_paused;
                        return (
                            <Card
                                key={c.chat_id}
                                className="bg-card/60 backdrop-blur transition-colors duration-150 hover:bg-card/80 cursor-pointer"
                                onClick={() => navigate(`/channels/${c.chat_id}`)}
                                data-testid={`channel-card-${c.chat_id}`}
                            >
                                <CardHeader className="pb-3">
                                    <div className="flex items-start justify-between gap-2">
                                        <div className="min-w-0">
                                            <CardTitle className="text-base truncate flex items-center gap-2">
                                                <Radio className="h-4 w-4 shrink-0 text-cyan-300" />
                                                <span className="truncate">{c.title}</span>
                                            </CardTitle>
                                            <div className="mt-1 flex items-center gap-2 flex-wrap">
                                                <span
                                                    className="text-[11px] font-mono text-muted-foreground"
                                                    dir="ltr"
                                                >
                                                    {c.chat_id}
                                                </span>
                                                {c.username && (
                                                    <Badge variant="outline" className="text-[10px]" dir="ltr">
                                                        @{c.username}
                                                    </Badge>
                                                )}
                                                <Badge variant="outline" className="text-[10px] border-cyan-500/20 text-cyan-300">
                                                    قناة
                                                </Badge>
                                            </div>
                                        </div>
                                        <HealthBadge score={c.health_score ?? 100} />
                                    </div>
                                </CardHeader>
                                <CardContent className="pt-0">
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-2 text-xs">
                                            {protectionActive ? (
                                                <>
                                                    <Lock className="h-3.5 w-3.5 text-rose-400" />
                                                    <span className="text-rose-300">حماية مفعّلة</span>
                                                </>
                                            ) : paused ? (
                                                <>
                                                    <Shield className="h-3.5 w-3.5 text-amber-400" />
                                                    <span className="text-amber-300">موقوف مؤقتاً</span>
                                                </>
                                            ) : (
                                                <>
                                                    <Shield
                                                        className={`h-3.5 w-3.5 ${
                                                            c.is_active
                                                                ? "text-emerald-400"
                                                                : "text-muted-foreground"
                                                        }`}
                                                    />
                                                    <span className="text-muted-foreground">
                                                        {c.is_active ? "حماية نشطة" : "غير نشطة"}
                                                    </span>
                                                </>
                                            )}
                                        </div>
                                        <ChevronLeft className="h-4 w-4 text-muted-foreground" />
                                    </div>
                                </CardContent>
                            </Card>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
